import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { supabase } from '@/integrations/supabase/client';

interface AdminAuthContextType {
  isAdmin: boolean;
  isSuperadmin: boolean;
  loading: boolean;
  refreshPermissions: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const useSuperadminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useSuperadminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { albumId, loading: albumLoading } = useAlbumContext();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkPermissions = async () => {

    if (!user?.id || !albumId) {
      setIsAdmin(false);
      setIsSuperadmin(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Check superadmin first
      const { data: superadminData, error: superadminError } = await supabase.rpc('is_superadmin', {
        album_id: albumId
      });



      const userIsSuperadmin = superadminData === true;
      setIsSuperadmin(userIsSuperadmin);

      // If user is superadmin, they automatically have admin privileges
      if (userIsSuperadmin) {
        setIsAdmin(true);
      } else {
        // Only check admin role if user is not superadmin
        const { data: adminData, error: adminError } = await supabase.rpc('is_admin', {
          album_id: albumId
        });

        if (adminError) {
          setIsAdmin(false);
        } else {
          setIsAdmin(adminData === true);
        }
      }
    } catch (error) {
      setIsAdmin(false);
      setIsSuperadmin(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshPermissions = async () => {
    await checkPermissions();
  };

  // Check permissions when user or albumId changes
  useEffect(() => {
    if (authLoading || albumLoading) {
      setLoading(true);
      return;
    }

    if (!user?.id || !albumId) {
      setIsAdmin(false);
      setIsSuperadmin(false);
      setLoading(false);
      return;
    }

    checkPermissions();
  }, [user?.id, albumId, authLoading, albumLoading]);

  // Reset state when user logs out
  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      setIsSuperadmin(false);
      setLoading(false);
    }
  }, [user?.id]);

  const value = {
    isAdmin,
    isSuperadmin,
    loading: authLoading || albumLoading || loading,
    refreshPermissions,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
